import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  MapPin, CalendarDays, Moon, Users, Download, Bird, Mail, Phone, Loader2,
  BedDouble, Compass, Ticket, Car, TrainFront, Plane, Sun, FileText, ChevronDown,
  Clock, Building2, ShieldCheck, Paperclip, Info, LifeBuoy, Hash, User, Utensils,
} from "lucide-react";
import PwaInstaller from "@/components/public/PwaInstaller";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const pub = axios.create({ baseURL: `${BACKEND}/api/public` });
const imgUrl = (u) => (!u ? "" : String(u).startsWith("/api/") ? `${BACKEND}${u}` : u);
const fileUrl = (u) => (!u ? "" : `${BACKEND}${u}`);
import { getBrand } from "@/lib/brand";

const C = { ink: "#1A1A1A", ink2: "#2C2C2C", sub: "#52525B", mute: "#A1A1AA", line: "#E4E4E7", bg: "#F9F8F6", surface: "#FFFFFF", navy: "#121B28", sand: "#F4EBD7", olive: "#8a9771" };
const SERIF = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const SANS = { fontFamily: "'Manrope', system-ui, sans-serif" };

const BOARD_LABELS = { sa: "Room only", bb: "Bed & breakfast", mp: "Half board", pc: "Full board" };
const TYPE_META = {
  alojamiento: { label: "Stay", Icon: BedDouble }, actividad: { label: "Experience", Icon: Compass },
  entradas: { label: "Tickets", Icon: Ticket }, transfer: { label: "Transfer", Icon: Car },
  rental_car: { label: "Self-Drive", Icon: Car },
  tren: { label: "Train", Icon: TrainFront }, vuelo: { label: "Flight", Icon: Plane }, free_day: { label: "Free day", Icon: Sun },
};

const fmtDate = (d) => { try { return new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); } catch { return d || ""; } };
const fmtWeekday = (d) => { try { return new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString("en-GB", { weekday: "long" }); } catch { return ""; } };
const fmtLong = (d) => { try { return new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "long" }); } catch { return d || ""; } };

const FontTag = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=Manrope:wght@300;400;500;600;700&display=swap');
    .et-serif{font-family:'Cormorant Garamond',Georgia,serif;}
    .et-sans{font-family:'Manrope',system-ui,-apple-system,sans-serif;}
    .et-noscroll::-webkit-scrollbar{display:none}
    .et-noscroll{-ms-overflow-style:none;scrollbar-width:none}
    html{scroll-behavior:smooth}

    /* Estilos para el HTML embebido del prefacio (Traveler Info).
       Preserva jerarquía visual con nuestros tokens de tipografía. */
    .td-preface-content{color:#2C2C2C;font-family:'Manrope',system-ui,sans-serif;font-size:15px;line-height:1.65;}
    .td-preface-content h2{font-family:'Cormorant Garamond',Georgia,serif;font-size:1.75rem;font-weight:600;color:#121B28;margin:2rem 0 0.75rem;letter-spacing:-0.01em;line-height:1.15;}
    .td-preface-content h2:first-child{margin-top:0;}
    .td-preface-content h3{font-family:'Manrope',system-ui,sans-serif;font-size:1rem;font-weight:700;color:#1A1A1A;text-transform:uppercase;letter-spacing:0.06em;margin:1.5rem 0 0.5rem;}
    .td-preface-content p{margin:0.5rem 0;}
    .td-preface-content ul,.td-preface-content ol{margin:0.5rem 0 0.75rem 1.25rem;padding-left:1rem;}
    .td-preface-content ul{list-style:disc;} .td-preface-content ol{list-style:decimal;}
    .td-preface-content li{margin:0.25rem 0;}
    .td-preface-content strong{color:#121B28;font-weight:600;}
    .td-preface-content em{color:#52525B;}
    .td-preface-content a{color:#8a9771;text-decoration:underline;text-underline-offset:2px;font-weight:600;}
    .td-preface-content a:hover{color:#121B28;}
  `}</style>
);

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }} className={className}>{children}</motion.div>
);

const Overline = ({ children, className = "" }) => (
  <p className={`et-sans text-[11px] font-semibold uppercase tracking-[0.22em] text-[#71717A] ${className}`}>{children}</p>
);

const ImageStrip = ({ images, alt, testid }) => {
  const imgs = (images || []).filter(Boolean).slice(0, 4);
  if (!imgs.length) return null;
  return (
    <div className={`grid gap-2 ${imgs.length === 1 ? "grid-cols-1" : "grid-cols-2"}`} data-testid={testid}>
      {imgs.map((u, i) => (
        <div key={i} className={`overflow-hidden rounded-sm ${imgs.length === 3 && i === 0 ? "col-span-2" : ""}`}>
          <img src={imgUrl(u)} alt={alt} loading="lazy" onError={(e) => { e.target.parentElement.style.display = "none"; }}
            className="h-full w-full object-cover transition-transform duration-[1200ms] hover:scale-105" style={{ aspectRatio: imgs.length === 1 ? "16/9" : "4/3" }} />
        </div>
      ))}
    </div>
  );
};

const VoucherButtons = ({ vouchers, testid }) => {
  if (!vouchers?.length) return null;
  return (
    <div className="mt-5 flex flex-wrap gap-2" data-testid={testid}>
      {vouchers.map((v, i) => (
        <a key={v.id || i} href={fileUrl(v.url)} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-${i}`}
          className="et-sans inline-flex items-center gap-2 rounded-full border border-[#121B28]/20 bg-white px-4 py-2 text-xs font-semibold text-[#121B28] transition-colors hover:bg-[#F4EBD7]">
          <Paperclip className="h-3.5 w-3.5" style={{ color: C.olive }} /> {v.filename || "Voucher"}
        </a>
      ))}
    </div>
  );
};

const OpsRow = ({ Icon, label, value }) => (
  <div className="flex gap-2.5">
    <Icon className="mt-0.5 h-4 w-4 flex-shrink-0" style={{ color: C.olive }} />
    <div className="min-w-0">
      <p className="et-sans text-[10px] font-semibold uppercase tracking-wider text-[#71717A]">{label}</p>
      <p className="et-sans whitespace-pre-line break-words text-sm font-light leading-relaxed text-[#2C2C2C]">{value}</p>
    </div>
  </div>
);

const OPS_ICONS = { MapPin, Car, Plane, Hash, User, Info, Clock, BedDouble, Utensils, Phone, TrainFront, Building2, Ticket };

const OpsBlock = ({ ops, testid }) => {
  const rows = (ops?.rows || []).filter((r) => r && String(r.value || "").trim());
  if (!rows.length) return null;
  return (
    <div className="mt-5 rounded-lg border border-[#E4E4E7] bg-[#F9F8F6] p-4" data-testid={testid}>
      <p className="et-sans mb-3 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#121B28]"><Info className="h-3.5 w-3.5" style={{ color: C.olive }} />Practical info</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {rows.map((r, i) => { const Icon = OPS_ICONS[r.icon] || Info; return <OpsRow key={i} Icon={Icon} label={r.label} value={r.value} />; })}
      </div>
    </div>
  );
};

const ServiceItem = ({ s, testid }) => {
  const meta = TYPE_META[s.type] || TYPE_META.actividad;
  const Icon = meta.Icon;
  const details = [s.hour, s.provider_name, s.room].filter(Boolean).join(" · ");
  return (
    <div className="flex flex-col gap-6 border-b border-[#E4E4E7] py-10 last:border-b-0 md:flex-row md:gap-12" data-testid={testid}>
      <div className="w-full flex-shrink-0 md:w-44">
        <p className="et-sans flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#71717A]">
          <Icon className="h-3.5 w-3.5" /> {meta.label}
        </p>
        {s.hour ? <p className="et-sans mt-2 flex items-center gap-1.5 text-sm font-medium text-[#1A1A1A]"><Clock className="h-3.5 w-3.5 text-[#A1A1AA]" />{s.hour}</p> : null}
      </div>
      <div className="min-w-0 flex-1">
        {s.name ? <h3 className="et-serif text-2xl font-medium leading-snug text-[#1A1A1A] md:text-3xl">{s.name}</h3> : null}
        {details ? <p className="et-sans mt-1 text-sm font-light text-[#52525B]">{details}</p> : null}
        {s.sales_description ? <div className="et-sans prose-sm mt-3 max-w-none text-base font-light leading-relaxed text-[#2C2C2C] [&_a]:underline [&_p]:mb-2 md:text-lg" dangerouslySetInnerHTML={{ __html: s.sales_description }} /> : null}
        <OpsBlock ops={s.ops} testid={`${testid}-ops`} />
        {s.images?.length ? <div className="mt-6"><ImageStrip images={s.images} alt={s.name} testid={`${testid}-img`} /></div> : null}
        <VoucherButtons vouchers={s.vouchers} testid={`${testid}-vouchers`} />
      </div>
    </div>
  );
};

export default function TravelDocViewer() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [state, setState] = useState("loading");
  const [active, setActive] = useState("");
  const sectionRefs = useRef({});
  const brand = getBrand(doc?.brand);

  const scrollTo = (id) => { const el = document.getElementById(id); if (el) window.scrollTo({ top: el.offsetTop - 76, behavior: "smooth" }); };

  useEffect(() => {
    let on = true;
    pub.get(`/td/${token}`).then(({ data }) => { if (on) { setDoc(data); setState("ok"); } })
      .catch(() => { if (on) setState("error"); });
    return () => { on = false; };
  }, [token]);

  // Carga del prefacio público (Traveler Info) — se muestra al inicio del TD.
  const [prefaceHtml, setPrefaceHtml] = useState("");
  useEffect(() => {
    let on = true;
    pub.get("/td-preface").then((r) => { if (on) setPrefaceHtml(r.data?.html || ""); }).catch(() => {});
    return () => { on = false; };
  }, []);

  // Colapsable: guardamos preferencia por token para no molestar al viajero
  // una vez la ha leído (pero le avisamos si intenta colapsarla sin leer).
  const [prefaceOpen, setPrefaceOpen] = useState(true);
  const [prefaceRead, setPrefaceRead] = useState(false);
  useEffect(() => {
    try {
      const read = localStorage.getItem(`td-preface-read-${token}`) === "1";
      setPrefaceRead(read);
      setPrefaceOpen(!read);   // si ya lo leyó, arranca colapsada
    } catch { /* localStorage no disponible → mantener defaults */ }
  }, [token]);
  const togglePreface = () => {
    if (prefaceOpen && !prefaceRead) {
      // Primera vez que colapsa: recordatorio + marcamos como leído.
      const ok = window.confirm(
        "It's important that you read this information before your trip.\n\n" +
        "Are you sure you want to collapse it? You can always re-open it above."
      );
      if (!ok) return;
      try { localStorage.setItem(`td-preface-read-${token}`, "1"); } catch { /* ignore */ }
      setPrefaceRead(true);
    }
    setPrefaceOpen((v) => !v);
  };

  useEffect(() => {
    if (doc?.title) document.title = `${doc.title} · Travel Document`;
    return () => { document.title = "SOFI 2.0 — Plataforma de gestión"; };
  }, [doc?.title]);

  useEffect(() => {
    if (state !== "ok") return;
    const obs = new IntersectionObserver(
      (entries) => { const vis = entries.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio); if (vis[0]) setActive(vis[0].target.id); },
      { rootMargin: "-45% 0px -50% 0px", threshold: [0, 0.25, 0.5, 1] }
    );
    Object.values(sectionRefs.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, [state, doc]);

  const nights = useMemo(() => (doc?.accommodations || []).reduce((s, a) => s + (a.nights || 0), 0), [doc]);
  const navItems = useMemo(() => {
    if (!doc) return [];
    const items = (doc.days || []).map((d, i) => ({ id: `day-${i}`, label: d.date ? fmtDate(d.date) : (d.label || `Day ${d.day_number || i + 1}`) }));
    if (prefaceHtml) items.unshift({ id: "traveler-info", label: "Info" });
    if ((doc.accommodations || []).length) items.push({ id: "stay", label: "Stay" });
    if ((doc.vouchers || []).length) items.push({ id: "vouchers", label: "Vouchers" });
    if ((doc.info_documents || []).length) items.push({ id: "info", label: "Docs" });
    return items;
  }, [doc, prefaceHtml]);

  if (state === "loading") return (
    <div className="flex min-h-screen items-center justify-center" style={{ backgroundColor: C.bg }}><Loader2 className="h-8 w-8 animate-spin" style={{ color: C.navy }} /></div>
  );
  if (state === "error") return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 px-6 text-center" style={{ backgroundColor: C.bg, color: C.ink }}>
      <FontTag /><Bird className="h-12 w-12" style={{ color: C.olive }} />
      <h1 className="et-serif text-3xl font-medium">Travel document not available</h1>
      <p className="et-sans font-light opacity-70">This link is invalid or has been unpublished.</p>
    </div>
  );

  const consultant = doc.consultant || {};

  return (
    <div className="et-sans min-h-screen" style={{ backgroundColor: C.bg, color: C.ink }} data-testid="travel-doc-viewer">
      <FontTag />

      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-[#E4E4E7] bg-white/90 backdrop-blur-md print:static" data-testid="td-header">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-5 py-3.5 md:px-8">
          <div className="flex h-9 flex-shrink-0 items-center rounded-md border border-[#E4E4E7] bg-white px-3">
            <img src={brand.logo_navy} alt={brand.name} className="h-4 w-auto sm:h-4.5" />
          </div>
          {navItems.length > 0 && (
            <nav className="et-noscroll mx-1 flex flex-1 items-center gap-4 overflow-x-auto md:gap-5 print:hidden" data-testid="td-daynav">
              {navItems.map((n) => (
                <button key={n.id} onClick={() => scrollTo(n.id)} data-testid={`td-nav-${n.id}`}
                  className={`et-sans whitespace-nowrap text-sm transition-colors ${active === n.id ? "font-semibold text-[#1A1A1A]" : "font-light text-[#A1A1AA] hover:text-[#52525B]"}`}>{n.label}</button>
              ))}
            </nav>
          )}
          <div className="flex flex-shrink-0 items-center gap-2">
            <a href={`${BACKEND}/api/public/td/${token}/pdf`} target="_blank" rel="noopener noreferrer" data-testid="td-print-btn"
              className="et-sans flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-[#1A1A1A] hover:opacity-70 print:hidden">
              <Download className="h-4 w-4" /> <span className="hidden sm:inline">Download PDF</span>
            </a>
            {brand.id !== "vdv" && (
              <PwaInstaller token={token} className="text-[#1A1A1A] hover:opacity-70" />
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative flex h-[78vh] min-h-[480px] w-full items-end overflow-hidden" style={{ backgroundColor: C.navy }} data-testid="td-hero">
        {doc.hero_image && <img src={imgUrl(doc.hero_image)} alt={doc.title} className="absolute inset-0 h-full w-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-black/15" />
        <div className="relative mx-auto w-full max-w-5xl px-5 pb-16 md:px-8">
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="et-sans mb-3 text-xs font-semibold uppercase tracking-[0.3em]" style={{ color: C.sand }}>
            Travel Document{doc.destination ? ` · ${doc.destination}` : ""}
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }}
            className="et-serif max-w-3xl text-5xl font-medium leading-[1.05] tracking-tight text-white md:text-7xl" data-testid="td-title">{doc.title}</motion.h1>
          <motion.div initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.12 }}
            className="et-sans mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm font-light text-white/90 md:text-base">
            {doc.start_date && <span className="flex items-center gap-2"><CalendarDays className="h-4 w-4" />{fmtDate(doc.start_date)} – {fmtDate(doc.end_date)}</span>}
            <span className="flex items-center gap-2"><Moon className="h-4 w-4" />{doc.duration_days} days{nights ? ` · ${nights} nights` : ""}</span>
            <span className="flex items-center gap-2"><Users className="h-4 w-4" />{doc.num_travelers} travellers</span>
          </motion.div>
        </div>
        <div className="absolute bottom-6 left-1/2 flex -translate-x-1/2 flex-col items-center gap-1 text-white/70 print:hidden">
          <span className="et-sans text-[11px] uppercase tracking-[0.2em]">Scroll to view</span><ChevronDown className="h-4 w-4 animate-bounce" />
        </div>
      </section>

      {/* Welcome */}
      <section className="mx-auto max-w-3xl px-5 py-16 text-center md:px-8" data-testid="td-welcome">
        <Reveal>
          <Overline>Everything for your trip</Overline>
          <p className="et-serif mt-4 text-2xl font-light leading-relaxed text-[#1A1A1A] md:text-3xl">
            Your travel documents are ready. Here you'll find your day-by-day itinerary, accommodation details, vouchers and everything you need for a smooth journey.
          </p>
        </Reveal>
      </section>

      <main className="mx-auto max-w-4xl px-5 md:px-12">
        {/* Traveler Info (prefacio colapsable) — se muestra ANTES del itinerario para
            que el viajero lo lea. Puede colapsarse tras un aviso de confirmación. */}
        {prefaceHtml && (
          <section
            id="traveler-info"
            ref={(el) => (sectionRefs.current["traveler-info"] = el)}
            className="border-t border-[#E4E4E7] pb-4 pt-16"
            data-testid="td-traveler-info"
          >
            <Reveal>
              <button
                type="button"
                onClick={togglePreface}
                aria-expanded={prefaceOpen}
                className="group flex w-full items-center justify-between gap-3 text-left transition"
                data-testid="td-traveler-info-toggle"
              >
                <div className="flex items-center gap-3">
                  <LifeBuoy className="h-5 w-5" style={{ color: C.olive }} />
                  <div>
                    <Overline>Read before you go</Overline>
                    <h2 className="et-serif mt-1 text-3xl font-medium tracking-tight text-[#1A1A1A] md:text-4xl">
                      Traveler Info
                    </h2>
                  </div>
                </div>
                <ChevronDown
                  className={`h-6 w-6 flex-shrink-0 text-[#52525B] transition-transform duration-300 ${prefaceOpen ? "rotate-180" : ""}`}
                />
              </button>

              {!prefaceOpen && (
                <div
                  className="mt-4 flex items-start gap-2.5 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                  data-testid="td-traveler-info-hint"
                >
                  <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <span>
                    <strong>It's important that you read this information before your trip.</strong>{" "}
                    Click above to re-open it whenever you need.
                  </span>
                </div>
              )}

              {prefaceOpen && (
                <div
                  className="td-preface-content mt-6 rounded-md border border-[#E4E4E7] bg-white px-5 py-6 md:px-8 md:py-8"
                  data-testid="td-traveler-info-content"
                  dangerouslySetInnerHTML={{ __html: prefaceHtml }}
                />
              )}
            </Reveal>
          </section>
        )}

        {/* Travellers */}
        {(doc.travelers || []).length > 0 && (
          <section className="border-t border-[#E4E4E7] pb-4 pt-16" data-testid="td-travellers">
            <Reveal>
              <Overline>Who's travelling</Overline>
              <h2 className="et-serif mt-2 text-3xl font-medium tracking-tight text-[#1A1A1A] md:text-4xl">Travellers</h2>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {doc.travelers.map((t, i) => (
                  <div key={i} className="flex items-center gap-3 border border-[#E4E4E7] bg-white px-5 py-4" data-testid={`td-traveller-${i}`}>
                    <Users className="h-5 w-5 flex-shrink-0" style={{ color: C.olive }} />
                    <div className="min-w-0">
                      <p className="et-sans truncate text-base font-semibold text-[#1A1A1A]">{t.name}</p>
                      <p className="et-sans truncate text-xs font-light text-[#52525B]">{[t.nationality, t.passport_number].filter(Boolean).join(" · ") || "—"}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </section>
        )}

        {/* Day by day */}
        {(doc.days || []).map((d, i) => (
          <section key={i} id={`day-${i}`} ref={(el) => (sectionRefs.current[`day-${i}`] = el)}
            className="border-t border-[#E4E4E7] pb-4 pt-20" data-testid={`td-day-${i}`}>
            <Reveal>
              {d.date && <Overline>{fmtWeekday(d.date)}</Overline>}
              <h2 className="et-serif text-4xl font-medium tracking-tight text-[#1A1A1A] md:text-5xl">
                {d.date ? fmtLong(d.date) : (d.label || d.city || `Day ${d.day_number || i + 1}`)}
              </h2>
              {d.city && <p className="et-sans mt-1 flex items-center gap-1.5 text-sm font-light text-[#52525B]"><MapPin className="h-3.5 w-3.5" />{d.city}</p>}
            </Reveal>
            <div className="mt-6">
              {(d.services || []).length === 0 && <p className="et-sans py-8 text-base font-light italic text-[#A1A1AA]">A free day to explore at your own pace.</p>}
              {(d.services || []).map((s, si) => <ServiceItem key={si} s={s} testid={`td-day-${i}-svc-${si}`} />)}
            </div>
          </section>
        ))}

        {/* Accommodations */}
        {(doc.accommodations || []).length > 0 && (
          <section id="stay" ref={(el) => (sectionRefs.current["stay"] = el)} className="border-t border-[#E4E4E7] pb-6 pt-20" data-testid="td-stays">
            <Reveal>
              <Overline>Where you'll stay</Overline>
              <h2 className="et-serif mt-2 text-4xl font-medium tracking-tight text-[#1A1A1A] md:text-5xl">Your accommodations</h2>
            </Reveal>
            <div className="mt-8 space-y-8">
              {doc.accommodations.map((a, i) => (
                <Reveal key={i} delay={0.03}>
                  <article className="border border-[#E4E4E7] bg-white p-6 md:p-10" data-testid={`td-stay-${i}`}>
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
                      <h3 className="et-serif text-3xl font-medium text-[#1A1A1A] md:text-4xl">{a.name}</h3>
                      <p className="et-sans flex items-center gap-3 text-sm font-light text-[#52525B]">
                        {a.city && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{a.city}</span>}
                        <span className="flex items-center gap-1"><Moon className="h-3.5 w-3.5" />{a.nights} {a.nights === 1 ? "night" : "nights"}</span>
                      </p>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span className="et-sans inline-block rounded-full border border-[#E4E4E7] px-3 py-0.5 text-[11px] font-medium uppercase tracking-wide text-[#52525B]">{BOARD_LABELS[a.board] || "Bed & breakfast"}</span>
                      {(a.date_from || a.date_to) && <span className="et-sans flex items-center gap-1.5 text-sm font-light text-[#52525B]"><CalendarDays className="h-3.5 w-3.5 text-[#A1A1AA]" />Check-in {fmtDate(a.date_from)} → Check-out {fmtDate(a.date_to)}</span>}
                      {a.room && <span className="et-sans flex items-center gap-1.5 text-sm font-light text-[#52525B]"><BedDouble className="h-3.5 w-3.5 text-[#A1A1AA]" />{a.room}</span>}
                    </div>
                    {(a.images || []).length > 0 && <div className="mt-6"><ImageStrip images={a.images} alt={a.name} testid={`td-stay-${i}-img`} /></div>}
                    {a.sales_description && <div className="et-sans prose-sm mt-6 max-w-none text-base font-light leading-relaxed text-[#2C2C2C] [&_p]:mb-2 md:text-lg" dangerouslySetInnerHTML={{ __html: a.sales_description }} />}
                    <OpsBlock ops={a.ops} testid={`td-stay-${i}-ops`} />
                    <VoucherButtons vouchers={a.vouchers} testid={`td-stay-${i}-vouchers`} />
                  </article>
                </Reveal>
              ))}
            </div>
          </section>
        )}

        {/* Flights & transport */}
        {doc.flight_details && (
          <section id="flights" className="border-t border-[#E4E4E7] pb-6 pt-20" data-testid="td-flights">
            <Reveal>
              <Overline>Getting there</Overline>
              <h2 className="et-serif mt-2 text-4xl font-medium tracking-tight text-[#1A1A1A] md:text-5xl">Flights &amp; transport</h2>
              <div className="mt-6 border border-[#E4E4E7] bg-white p-6 md:p-8">
                <p className="et-sans flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-[#52525B]"><Plane className="h-4 w-4" style={{ color: C.olive }} />Your flights</p>
                <div className="et-sans mt-3 whitespace-pre-line text-base font-light leading-relaxed text-[#2C2C2C]">{doc.flight_details}</div>
              </div>
            </Reveal>
          </section>
        )}

        {/* Vouchers */}
        {(doc.vouchers || []).length > 0 && (
          <section id="vouchers" ref={(el) => (sectionRefs.current["vouchers"] = el)} className="border-t border-[#E4E4E7] pb-6 pt-20" data-testid="td-vouchers">
            <Reveal>
              <Overline>Your documents</Overline>
              <h2 className="et-serif mt-2 text-4xl font-medium tracking-tight text-[#1A1A1A] md:text-5xl">Vouchers</h2>
              <p className="et-sans mt-2 text-base font-light text-[#52525B]">Show these to your suppliers on arrival. Tap to open or download.</p>
            </Reveal>
            <div className="mt-8 space-y-3">
              {doc.vouchers.map((v, i) => {
                const meta = TYPE_META[v.type] || TYPE_META.actividad;
                const Icon = meta.Icon;
                return (
                  <Reveal key={v.id || i} delay={0.02}>
                    <a href={fileUrl(v.url)} target="_blank" rel="noopener noreferrer" data-testid={`td-voucher-${i}`}
                      className="flex items-center justify-between gap-4 border border-[#E4E4E7] bg-white px-5 py-4 transition-colors hover:bg-[#F4EBD7]/40">
                      <div className="flex min-w-0 items-center gap-3">
                        <Icon className="h-5 w-5 flex-shrink-0" style={{ color: C.olive }} />
                        <div className="min-w-0">
                          <p className="et-sans truncate text-base font-semibold text-[#1A1A1A]">{v.service || v.filename}</p>
                          <p className="et-sans truncate text-xs font-light text-[#52525B]">{[meta.label, v.provider_name].filter(Boolean).join(" · ") || v.filename}</p>
                        </div>
                      </div>
                      <span className="et-sans flex flex-shrink-0 items-center gap-1.5 text-sm font-semibold text-[#121B28]"><Download className="h-4 w-4" />Open</span>
                    </a>
                  </Reveal>
                );
              })}
            </div>
          </section>
        )}

        {/* Practical info */}
        {(doc.info_documents || []).length > 0 && (
          <section id="info" ref={(el) => (sectionRefs.current["info"] = el)} className="border-t border-[#E4E4E7] pb-16 pt-20" data-testid="td-info">
            <Reveal>
              <Overline>Good to know</Overline>
              <h2 className="et-serif mt-2 text-4xl font-medium tracking-tight text-[#1A1A1A] md:text-5xl">Practical information</h2>
            </Reveal>
            <div className="mt-8 space-y-4">
              {doc.info_documents.map((s, i) => (
                <Reveal key={i} delay={0.02}>
                  <div className="border border-[#E4E4E7] bg-white p-6 md:p-8" data-testid={`td-info-${i}`}>
                    <h3 className="et-serif flex items-center gap-2 text-2xl font-medium text-[#1A1A1A]"><Info className="h-5 w-5" style={{ color: C.olive }} />{s.title}</h3>
                    <div className="et-sans mt-2 whitespace-pre-line text-base font-light leading-relaxed text-[#2C2C2C]">{s.body}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </section>
        )}
      </main>

      {/* Specialist / emergency contact */}
      {consultant.name && (
        <section className="mx-auto max-w-4xl px-5 pb-8 md:px-12" data-testid="td-specialist">
          <div className="flex flex-col items-center gap-6 border border-[#E4E4E7] bg-white p-8 text-center md:flex-row md:gap-10 md:p-10 md:text-left">
            <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: `${C.olive}22` }}>
              <LifeBuoy className="h-9 w-9" style={{ color: C.olive }} />
            </div>
            <div className="flex-1">
              <Overline>Your travel specialist · here 24/7</Overline>
              <h3 className="et-serif mt-1 text-3xl font-medium text-[#1A1A1A]">{consultant.name}</h3>
              {consultant.role && <p className="et-sans text-sm font-light text-[#52525B]">{consultant.role}</p>}
              <div className="mt-4 flex flex-col items-center gap-3 sm:flex-row md:justify-start">
                {consultant.email && <a href={`mailto:${consultant.email}`} className="et-sans flex items-center gap-2 text-sm font-light text-[#1A1A1A] hover:opacity-70"><Mail className="h-4 w-4 text-[#A1A1AA]" />{consultant.email}</a>}
                {consultant.phone && <a href={`tel:${consultant.phone}`} className="et-sans flex items-center gap-2 text-sm font-light text-[#1A1A1A] hover:opacity-70"><Phone className="h-4 w-4 text-[#A1A1AA]" />{consultant.phone}</a>}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="px-5 py-16 text-white/80" style={{ backgroundColor: C.navy }} data-testid="td-footer">
        <div className="mx-auto max-w-3xl border-t border-white/10 pt-12 text-center">
          <p className="et-serif text-3xl font-light italic text-white">Have a wonderful journey</p>
          <div className="mx-auto mt-10 flex w-fit flex-col items-center gap-4">
            <span className="et-sans text-[11px] uppercase tracking-[0.3em] text-white/50">With you all the way</span>
            <img src={brand.logo_sand} alt={brand.name} className="h-12 w-auto sm:h-14" />
          </div>
        </div>
      </footer>
    </div>
  );
}
